/*
  Communite System Builder
  Device-aware, step-based accessory selection with one bundled Cart API request.
*/

(() => {
  const selector = '[data-communite-system-builder]'
  const stepOrder = ['device', 'protection', 'screen', 'power', 'audio', 'summary']
  const instances = new WeakMap()

  const locale = () => ({
    count: window.theme?.locales?.builder?.count || '[count] products selected',
    variantRequired: window.theme?.locales?.builder?.variant_required || 'Select a variant before continuing.',
    inStock: window.theme?.locales?.product?.in_stock || 'In stock',
    soldOut: window.theme?.locales?.product?.sold_out || 'Sold out',
    variant: window.theme?.locales?.builder?.variant || 'Select a variant',
    edit: window.theme?.locales?.builder?.edit || 'Edit',
    unknownVariant: window.theme?.locales?.builder?.unknown_variant || 'Selected variant'
  })

  class CommuniteSystemBuilder {
    constructor (section) {
      this.section = section
      this.controller = new AbortController()
      this.signal = this.controller.signal
      this.cards = [...section.querySelectorAll('[data-system-builder-card]')]
      this.panels = [...section.querySelectorAll('[data-builder-step-panel]')]
      this.navButtons = [...section.querySelectorAll('[data-builder-step-nav]')]
      this.submitButton = section.querySelector('[data-builder-submit]')
      this.buttonLabel = section.querySelector('[data-builder-button-label]')
      this.totalElement = section.querySelector('[data-builder-total]')
      this.countElement = section.querySelector('[data-builder-count]')
      this.messageElement = section.querySelector('[data-builder-message]')
      this.summaryList = section.querySelector('[data-builder-summary-list]')
      this.summaryEmpty = section.querySelector('[data-builder-summary-empty]')
      this.defaultButtonLabel = this.buttonLabel?.textContent.trim() || ''
      this.loadingLabel = section.dataset.loadingLabel || this.defaultButtonLabel
      this.selectionMessage = section.dataset.selectionMessage || ''
      this.deviceMessage = section.dataset.deviceMessage || this.selectionMessage
      this.successMessage = section.dataset.successMessage || ''
      this.errorMessage = section.dataset.errorMessage || ''
      this.stockErrorMessage = section.dataset.stockErrorMessage || this.errorMessage
      this.partialErrorMessage = section.dataset.partialErrorMessage || this.errorMessage
      this.openCartDrawer = section.dataset.openCartDrawer === 'true'
      this.designMode = section.dataset.designMode === 'true'
      this.busy = false
      this.device = window.CommuniteDeviceState?.current || null
      this.previousDeviceKey = this.device?.key || ''
      this.activeStep = 'device'

      this.prepareCards()
      this.bindEvents()
      this.applyDevice(this.device, false)
      this.showStep(this.device ? 'protection' : 'device', false)
      this.updateSummary()
    }

    prepareCards () {
      this.cards.forEach(card => {
        const variantsElement = card.querySelector('[data-builder-variants]')

        try {
          card.builderVariants = variantsElement ? JSON.parse(variantsElement.textContent) : []
        } catch {
          card.builderVariants = []
        }

        this.updateCard(card)
      })

      stepOrder.slice(1, -1).forEach(step => {
        this.cards
          .filter(card => card.dataset.builderStep === step && card.querySelector('[data-builder-select]')?.checked)
          .slice(1)
          .forEach(card => {
            card.querySelector('[data-builder-select]').checked = false
            this.updateCard(card)
          })
      })
    }

    bindEvents () {
      this.cards.forEach(card => {
        card.querySelector('[data-builder-select]')?.addEventListener('change', event => {
          this.clearCardError(card)

          if (event.target.checked) {
            this.cards
              .filter(candidate => candidate !== card && candidate.dataset.builderStep === card.dataset.builderStep)
              .forEach(candidate => {
                const input = candidate.querySelector('[data-builder-select]')
                if (input) input.checked = false
                this.updateCard(candidate)
              })
          }

          this.updateCard(card)
          this.updateSummary()
        }, { signal: this.signal })

        card.querySelector('[data-builder-variant]')?.addEventListener('change', () => {
          this.clearCardError(card)
          this.updateCard(card)
          this.updateSummary()
        }, { signal: this.signal })
      })

      this.navButtons.forEach(button => {
        button.addEventListener('click', () => this.showStep(button.dataset.builderStepNav), {
          signal: this.signal
        })
      })

      this.section.querySelectorAll('[data-builder-next]').forEach(button => {
        button.addEventListener('click', () => this.moveStep(1), { signal: this.signal })
      })

      this.section.querySelectorAll('[data-builder-prev]').forEach(button => {
        button.addEventListener('click', () => this.moveStep(-1), { signal: this.signal })
      })

      this.summaryList?.addEventListener('click', event => {
        const edit = event.target.closest('[data-builder-edit-step]')
        if (edit) this.showStep(edit.dataset.builderEditStep)
      }, { signal: this.signal })

      this.submitButton?.addEventListener('click', () => this.addSelectedItems(), { signal: this.signal })

      document.addEventListener('cm:device:change', event => {
        this.applyDevice(event.detail?.device || null)
      }, { signal: this.signal })
    }

    getVariant (card) {
      const input = card.querySelector('[data-builder-variant]')
      const variantId = input?.value
      if (!variantId) return null

      return card.builderVariants.find(variant => String(variant.id) === String(variantId)) || null
    }

    isCompatible (card) {
      if (!this.device) return false
      const keys = (card.dataset.compatibleDeviceKeys || '').split('|').filter(Boolean)
      return keys.includes(this.device.key)
    }

    applyDevice (device, announce = true) {
      const nextKey = device?.key || ''
      const changed = Boolean(this.previousDeviceKey && this.previousDeviceKey !== nextKey)

      this.device = device

      if (changed) {
        this.cards.forEach(card => {
          const checkbox = card.querySelector('[data-builder-select]')
          if (checkbox) checkbox.checked = false
          this.clearCardError(card)
        })
      }

      this.previousDeviceKey = nextKey

      this.navButtons.forEach(button => {
        const isDeviceStep = button.dataset.builderStepNav === 'device'
        button.disabled = !device && !isDeviceStep
      })

      const deviceNext = this.section.querySelector('[data-builder-step-panel="device"] [data-builder-next]')
      if (deviceNext) deviceNext.disabled = !device

      this.cards.forEach(card => {
        const compatible = this.isCompatible(card)
        const checkbox = card.querySelector('[data-builder-select]')

        card.classList.toggle('is-device-locked', !device)
        card.classList.toggle('is-incompatible', Boolean(device && !compatible))
        card.hidden = Boolean(device && !compatible && !this.designMode)

        if (!device || !compatible) {
          if (checkbox) checkbox.checked = false
        }

        this.updateCard(card)
      })

      this.updateCompatibilityEmptyStates()
      this.updateSummary()

      if (!device) {
        this.showStep('device', false)
        if (announce) this.showMessage(this.deviceMessage)
      } else if (announce) {
        this.showMessage('')
      }
    }

    updateCompatibilityEmptyStates () {
      stepOrder.slice(1, -1).forEach(step => {
        const panel = this.panelFor(step)
        const empty = panel?.querySelector('[data-builder-compatible-empty]')
        if (!empty) return

        const available = this.cards.some(card => (
          card.dataset.builderStep === step &&
          !card.hidden &&
          card.dataset.productId &&
          this.isCompatible(card)
        ))

        empty.hidden = available || !this.device
      })
    }

    updateCard (card) {
      const checkbox = card.querySelector('[data-builder-select]')
      const variantInput = card.querySelector('[data-builder-variant]')
      const variant = this.getVariant(card)
      const price = card.querySelector('[data-builder-price]')
      const comparePrice = card.querySelector('[data-builder-compare-price]')
      const stock = card.querySelector('[data-builder-stock]')
      const hasProduct = Boolean(card.dataset.productId)
      const productAvailable = card.dataset.productAvailable === 'true'
      const compatible = this.isCompatible(card)
      const available = Boolean(variant?.available && compatible)
      const selectable = available && !this.busy
      const copy = locale()

      if (variant && price) {
        price.textContent = this.formatMoney(variant.price)

        if (comparePrice && variant.compare_at_price > variant.price) {
          comparePrice.textContent = this.formatMoney(variant.compare_at_price)
          comparePrice.hidden = false
        } else if (comparePrice) {
          comparePrice.textContent = ''
          comparePrice.hidden = true
        }
      }

      if (stock) {
        if (!this.device) {
          stock.textContent = this.deviceMessage
        } else if (!compatible) {
          stock.textContent = window.theme?.locales?.builder?.incompatible || 'Not compatible'
        } else if (variant) {
          stock.textContent = variant.available ? copy.inStock : copy.soldOut
        } else if (hasProduct && variantInput?.tagName === 'SELECT') {
          stock.textContent = productAvailable ? copy.variant : copy.soldOut
        }

        stock.classList.toggle('is-unavailable', !selectable)
      }

      if (checkbox) {
        checkbox.disabled = !selectable
        if (!available) checkbox.checked = false
      }

      if (variantInput?.tagName === 'SELECT') {
        variantInput.disabled = this.busy || !compatible || !productAvailable
      }

      card.classList.toggle('is-selected', Boolean(checkbox?.checked))
    }

    showStep (step, focus = true) {
      if (!stepOrder.includes(step)) return

      if (!this.device && step !== 'device') {
        step = 'device'
        this.showMessage(this.deviceMessage, 'error')
      }

      this.activeStep = step

      this.panels.forEach(panel => {
        const active = panel.dataset.builderStepPanel === step
        panel.hidden = !active
        panel.setAttribute('aria-hidden', String(!active))
      })

      this.navButtons.forEach(button => {
        const active = button.dataset.builderStepNav === step
        button.classList.toggle('is-active', active)
        if (active) {
          button.setAttribute('aria-current', 'step')
        } else {
          button.removeAttribute('aria-current')
        }
      })

      if (step === 'summary') this.renderSummary()

      if (focus) {
        const panel = this.panelFor(step)
        window.requestAnimationFrame(() => panel?.focus())
      }
    }

    panelFor (step) {
      return this.panels.find(panel => panel.dataset.builderStepPanel === step)
    }

    moveStep (direction) {
      const currentIndex = stepOrder.indexOf(this.activeStep)
      const nextIndex = Math.min(stepOrder.length - 1, Math.max(0, currentIndex + direction))
      this.showStep(stepOrder[nextIndex])
    }

    selectedCards () {
      return this.cards.filter(card => card.querySelector('[data-builder-select]')?.checked)
    }

    selections () {
      return this.selectedCards()
        .map(card => ({ card, variant: this.getVariant(card) }))
        .filter(selection => selection.variant)
    }

    updateSummary (preserveMessage = false) {
      const selectedCards = this.selectedCards()
      const validSelections = this.selections().filter(selection => selection.variant.available)
      const total = validSelections.reduce((sum, selection) => sum + Number(selection.variant.price), 0)
      const count = selectedCards.length
      const countTemplate = locale().count

      if (this.countElement) {
        this.countElement.textContent = countTemplate.split('[count]').join(String(count))
      }
      if (this.totalElement) this.totalElement.textContent = this.formatMoney(total)
      if (this.submitButton) {
        this.submitButton.disabled = this.busy || !this.device || count === 0 || validSelections.length !== count
      }

      this.renderSummary()

      if (!this.device) {
        this.showMessage(this.deviceMessage)
      } else if (!count) {
        this.showMessage(this.selectionMessage)
      } else if (validSelections.length !== count) {
        this.showMessage(locale().variantRequired, 'error')
      } else if (!this.busy && !preserveMessage) {
        this.showMessage('')
      }
    }

    renderSummary () {
      if (!this.summaryList || !this.summaryEmpty) return

      const selections = this.selections()
      this.summaryList.replaceChildren()
      this.summaryEmpty.hidden = selections.length > 0

      selections.forEach(({ card, variant }) => {
        const row = document.createElement('div')
        const copy = document.createElement('div')
        const group = document.createElement('strong')
        const product = document.createElement('span')
        const variantName = document.createElement('small')
        const price = document.createElement('b')
        const edit = document.createElement('button')

        row.className = 'communite-system-builder__summary-row'
        row.setAttribute('role', 'listitem')
        copy.className = 'communite-system-builder__summary-copy'
        group.textContent = card.dataset.builderGroup || card.dataset.builderStep
        product.textContent = card.dataset.builderProductTitle || locale().unknownVariant
        variantName.textContent = variant.title || locale().unknownVariant
        price.textContent = this.formatMoney(variant.price)
        edit.type = 'button'
        edit.dataset.builderEditStep = card.dataset.builderStep
        edit.textContent = locale().edit

        copy.append(group, product, variantName)
        row.append(copy, price, edit)
        this.summaryList.append(row)
      })
    }

    buildItems (selections) {
      const variantIds = new Set()

      return selections.reduce((items, { card, variant }) => {
        const id = Number(variant.id)
        if (variantIds.has(id)) return items

        variantIds.add(id)
        items.push({
          id,
          quantity: 1,
          properties: {
            _communite_builder: this.section.id,
            _communite_device: this.device.key,
            _communite_step: card.dataset.builderStep
          }
        })
        return items
      }, [])
    }

    async verifyStock (selections) {
      const root = window.Shopify?.routes?.root || '/'

      const results = await Promise.all(selections.map(async selection => {
        try {
          const response = await fetch(`${root}variants/${selection.variant.id}.js`, {
            headers: { Accept: 'application/json' }
          })

          if (!response.ok) return { ...selection, valid: false }

          const latest = await response.json()
          return {
            ...selection,
            valid: latest.available === true,
            latest
          }
        } catch {
          return { ...selection, valid: false }
        }
      }))

      return results.filter(result => !result.valid)
    }

    async addSelectedItems () {
      if (this.busy) return

      if (!this.device) {
        this.showStep('device')
        this.showMessage(this.deviceMessage, 'error', true)
        return
      }

      const selections = this.selections()
      if (!selections.length) {
        this.showMessage(this.selectionMessage, 'error', true)
        return
      }

      this.clearErrors()
      this.setBusy(true)

      try {
        const invalidSelections = await this.verifyStock(selections)

        if (invalidSelections.length > 0) {
          invalidSelections.forEach(selection => {
            this.markCardError(selection.card, this.stockErrorMessage)
          })
          this.showStep(invalidSelections[0].card.dataset.builderStep, false)
          this.showMessage(this.stockErrorMessage, 'error', true)
          return
        }

        if (typeof window.CommuniteCart?.addItems !== 'function') {
          throw new Error(this.errorMessage)
        }

        const items = this.buildItems(selections)
        const response = await window.CommuniteCart.addItems(items)
        const addedIds = new Set((response.items || []).map(item => String(item.variant_id || item.id)))
        const missingSelections = addedIds.size > 0
          ? selections.filter(selection => !addedIds.has(String(selection.variant.id)))
          : []

        if (missingSelections.length > 0) {
          missingSelections.forEach(selection => {
            this.markCardError(selection.card, this.partialErrorMessage)
          })
          this.showStep(missingSelections[0].card.dataset.builderStep, false)
          this.showMessage(this.partialErrorMessage, 'error', true)
        } else {
          this.showMessage(this.successMessage, 'success', true)
        }

        if (this.openCartDrawer) {
          window.requestAnimationFrame(() => window.CommuniteCart.showDrawer?.())
        }
      } catch (error) {
        const detail = error instanceof Error && error.message && error.message !== this.errorMessage
          ? ` ${error.message}`
          : ''
        this.showMessage(`${this.errorMessage}${detail}`, 'error', true)
      } finally {
        this.setBusy(false)
      }
    }

    clearErrors () {
      this.cards.forEach(card => this.clearCardError(card))
    }

    clearCardError (card) {
      const error = card.querySelector('[data-builder-card-error]')
      card.classList.remove('has-error')
      if (error) {
        error.hidden = true
        error.textContent = ''
      }
    }

    markCardError (card, message) {
      const error = card.querySelector('[data-builder-card-error]')
      card.classList.add('has-error')
      if (error) {
        error.textContent = message
        error.hidden = false
      }
    }

    setBusy (busy) {
      this.busy = busy
      this.section.setAttribute('aria-busy', String(busy))
      this.submitButton?.setAttribute('aria-busy', String(busy))
      this.submitButton?.classList.toggle('is-loading', busy)

      if (this.buttonLabel) {
        this.buttonLabel.textContent = busy ? this.loadingLabel : this.defaultButtonLabel
      }

      this.cards.forEach(card => this.updateCard(card))
      this.updateSummary(true)
    }

    showMessage (message, type = '', focus = false) {
      if (!this.messageElement) return

      this.messageElement.textContent = message
      this.messageElement.classList.toggle('is-error', type === 'error')
      this.messageElement.classList.toggle('is-success', type === 'success')

      if (focus) {
        window.requestAnimationFrame(() => this.messageElement.focus())
      }
    }

    formatMoney (cents) {
      if (typeof window.Shopify?.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, window.Shopify.moneyFormat)
      }

      return new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', {
        style: 'currency',
        currency: 'TRY'
      }).format(Number(cents) / 100)
    }

    selectBlock (target) {
      const card = target.matches?.('[data-system-builder-card]')
        ? target
        : target.querySelector?.('[data-system-builder-card]')

      if (card) this.showStep(card.dataset.builderStep)
    }

    destroy () {
      this.controller.abort()
      instances.delete(this.section)
    }
  }

  const rootsWithin = (scope) => {
    const roots = []

    if (scope?.matches?.(selector)) roots.push(scope)
    scope?.querySelectorAll?.(selector).forEach(section => roots.push(section))
    return roots
  }

  const init = (scope = document) => {
    rootsWithin(scope).forEach(section => {
      if (!instances.has(section)) {
        instances.set(section, new CommuniteSystemBuilder(section))
      }
    })
  }

  const destroy = (scope) => {
    rootsWithin(scope).forEach(section => instances.get(section)?.destroy())
  }

  window.CommuniteSystemBuilder = { init, destroy, version: '2' }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true })
  } else {
    init(document)
  }

  document.addEventListener('shopify:section:load', event => init(event.target))
  document.addEventListener('shopify:section:unload', event => destroy(event.target))
  document.addEventListener('shopify:section:select', event => {
    init(event.target)
    event.target.querySelector?.('[data-builder-step-nav]')?.focus()
  })
  document.addEventListener('shopify:block:select', event => {
    const section = event.target.closest?.(selector)
    if (section) instances.get(section)?.selectBlock(event.target)
  })
})()
